import { NavLink } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 樱花动漫',
    description: '樱花动漫是国内领先的日本漫画在线阅读平台，成立于2018年。了解我们的品牌故事、用户评价和联系方式。',
    url: 'https://sakuracomic.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: '樱花动漫',
      description: aboutInfo.brandIntro,
      foundingDate: '2018',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CN',
        addressRegion: '上海市',
        addressLocality: '浦东新区',
        streetAddress: '张江高科技园区樱花路888号',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const mergedSchema = { ...aboutSchema, ...faqSchema };

  return (
    <>
      <SEOHead
        title="关于我们_樱花动漫_免费日本漫画在线阅读平台"
        description="樱花动漫是国内领先的日本漫画在线阅读平台，成立于2018年。了解我们的品牌故事、发展历程、用户口碑评价及联系方式。收录超过5000部漫画，服务500万+动漫爱好者。"
        keywords="樱花动漫,关于我们,漫画平台介绍,品牌故事,联系方式"
        canonicalUrl="https://sakuracomic.cn/about"
        schema={mergedSchema}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>
      </div>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-950">
              关于樱花动漫
            </h1>
            <p className="mt-6 text-sm md:text-base text-foreground-700 leading-relaxed">
              {aboutInfo.brandIntro}
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              {aboutInfo.mission}
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 text-center mb-10">
            发展历程
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
              {aboutInfo.history.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex items-center gap-6 mb-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:flex flex-1 justify-end">
                    <div className={index % 2 === 0 ? 'text-right' : 'text-left'}>
                      <p className="text-lg font-heading font-semibold text-accent-500">{item.year}</p>
                      <p className="text-sm text-foreground-600 mt-1 max-w-[200px]">{item.event}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50 flex-shrink-0 z-10"></div>
                  <div className="flex-1 md:hidden">
                    <p className="text-base font-heading font-semibold text-accent-500">{item.year}</p>
                    <p className="text-sm text-foreground-600 mt-0.5">{item.event}</p>
                  </div>
                  <div className="hidden md:block flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 text-center mb-10">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {aboutInfo.reviews.map((review) => (
              <div
                key={review.id}
                className="p-5 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src={review.avatar}
                      alt={review.username}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{review.username}</p>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-[10px] ${
                            i < review.rating ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-foreground-600'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <span className="ml-auto text-xs text-foreground-600 whitespace-nowrap">{review.date}</span>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">邮箱</p>
                    <p className="text-sm text-foreground-950">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">电话</p>
                    <p className="text-sm text-foreground-950">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">地址</p>
                    <p className="text-sm text-foreground-950">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 flex-shrink-0">
                    <i className="ri-weibo-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">微博</p>
                    <p className="text-sm text-foreground-950">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-600">微信</p>
                    <p className="text-sm text-foreground-950">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-[400px]">
              <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3412.123456789!2d121.589!3d31.205!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDEyJzE4LjAiTiAxMjHCsDM1JzIwLjQiRQ!5e0!3m2!1szh-CN!2scn!4v1620000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="樱花动漫公司位置"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 text-center mb-10">
            常见问题
          </h2>
          <div className="space-y-3">
            {aboutInfo.faqs.map((faq, index) => (
              <details
                key={index}
                className="group border border-background-200/70 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                  <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-600 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-accent-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-background-50">
            开始你的漫画之旅
          </h2>
          <p className="mt-3 text-sm text-background-50/80">
            超过5000部精选日本漫画，等你来发现
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <NavLink
              to="/category/shonen"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-50 text-accent-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-compass-3-line"></i>
              浏览漫画
            </NavLink>
            <NavLink
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-50/20 text-background-50 text-sm font-medium hover:bg-background-50/30 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
}